import { useMemo, useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GlobalContext, LoaderContext } from "./contexts";
import { isLoggedInSelector } from "@/features/auth/store/authSlice";
import LoaderDiv from "@/shared/components/LoaderDiv";
import { CircularProgress } from "@mui/material";
import {
  userBrandsSelector,
  selectedBrandSelector,
  getPageSetupThunk,
  getBrandsThunk,
} from "@/shared/store/brandSlice";
import {
  loggedInUserRoleSelector,
  loggedInUserSelector,
} from "@/features/profile/store/userSlice";
import { USER_ROLE } from "@/config/constants";
import { ROLES } from "@/helpers/routes/rolesConfig";

type UserRecord = Record<string, any>;
type BrandRecord = Record<string, any>;

type RootState = {
  auth: {
    isUserLoggedIn?: boolean;
  };
  brand: {
    selectedBrand?: BrandRecord | null;
    userBrands?: BrandRecord[];
  };
  user: {
    role?: string;
    user?: UserRecord;
  };
};

type ProviderProps = {
  children: ReactNode;
};

type BrandRequestData = {
  fk_company_id: number;
  fk_participant_id: number;
  id?: string | number;
};

type DispatchAction = (_action: unknown) => unknown;
type RootSelector<T> = (_state: RootState) => T;

/**
 * @method GlobalContextProvider
 * @description Global context provider
 */
export const GlobalContextProvider = ({ children }: ProviderProps) => {
  const dispatch = useDispatch() as DispatchAction;
  const loggedInUser = useSelector((state: RootState) =>
    (loggedInUserSelector as unknown as RootSelector<UserRecord | undefined>)(
      state
    )
  );
  const loggedInUserRole = useSelector((state: RootState) =>
    (loggedInUserRoleSelector as unknown as RootSelector<string | undefined>)(
      state
    )
  );
  const selectedBrand = useSelector((state: RootState) =>
    (
      selectedBrandSelector as unknown as RootSelector<
        BrandRecord | null | undefined
      >
    )(state)
  );
  const isLoggedIn = useSelector((state: RootState) =>
    (isLoggedInSelector as RootSelector<boolean | undefined>)(state)
  );
  const userBrands = useSelector((state: RootState) =>
    (userBrandsSelector as unknown as RootSelector<BrandRecord[] | undefined>)(
      state
    )
  );
  const getPageSetup = useCallback(
    (id: string | number) => dispatch(getPageSetupThunk(id)),
    [dispatch]
  );
  const getBrands = useCallback(
    (data: BrandRequestData) => dispatch(getBrandsThunk(data)),
    [dispatch]
  );

  useEffect(() => {
    /** Call API related to selected brand  */

    if (isLoggedIn && selectedBrand && loggedInUser && loggedInUser?.id) {
      // if (loggedInUserRole === "PR" || loggedInUserRole === "PC") {
      // }
      let getBrandsRequestData: BrandRequestData = {
        fk_participant_id: 0,
        fk_company_id: 0,
      };

      if (ROLES.PARTICIPANT.includes(loggedInUserRole)) {
        getBrandsRequestData.fk_participant_id =
          loggedInUser?.effective_participant_id ||
          (loggedInUser?.participantData
            ? loggedInUser?.participantData?.id
            : 0);
      } else if (loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME) {
        getBrandsRequestData.fk_company_id = loggedInUser?.companyData
          ? loggedInUser?.companyData?.id
          : 0;
      } else if (
        loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
        loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME
      ) {
        getBrandsRequestData.fk_company_id = loggedInUser?.companyData
          ? loggedInUser?.companyData?.fk_parent_company_id
          : 0;
      } else {
        getBrandsRequestData.id = selectedBrand?.id;
      }

      getBrands(getBrandsRequestData);

      getPageSetup(selectedBrand?.id);
    }
    // Preserve the existing selected-brand-only refresh behavior during migration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  // useEffect(() => {
  //   if (selectedBrand?.id && !isThemeLoading) {
  //     getTheme(selectedBrand?.id);
  //   }
  // }, [selectedBrand?.id, isThemeLoading]);

  const value = useMemo(
    () => ({
      loggedInUser,
      loggedInUserRole,
      selectedBrand,
      isLoggedIn,
      userBrands,
    }),
    [loggedInUser, loggedInUserRole, selectedBrand, isLoggedIn, userBrands]
  );

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
};

/**
 * @method LoaderProvider
 * @description Loader context provider
 */

export const LoaderProvider = ({ children }: ProviderProps) => {
  const [show, setShow] = useState<boolean | number>(false);
  return (
    <>
      <LoaderDiv show={show ? true : false} filled={show === 1 ? true : false}>
        <CircularProgress
          style={{
            color:
              show === 1
                ? "var(--palette-common-black)"
                : "var(--neutral-white)",
          }}
          sx={{
            // Keep filled-state black; overlay white follows the app foundation.
            color:
              show === 1
                ? "var(--palette-common-black)"
                : "var(--neutral-white) !important",
            "& .MuiCircularProgress-circle": {
              stroke:
                show === 1
                  ? "var(--palette-common-black)"
                  : "var(--neutral-white) !important",
            },
          }}
        />
        {/* <h6 className="my-2">Loading...</h6> */}
      </LoaderDiv>

      <LoaderContext.Provider value={setShow}>
        {children}
      </LoaderContext.Provider>
    </>
  );
};
